import { memo, useCallback, useRef } from 'react';
import type { ResourceResponse } from 'types';
import { useTypedSelector } from 'hooks';
import { SetupIdentifier } from 'consts/setups';
import { isFormChanged } from 'util/forms';
import { when } from 'util/support';
import { selectIsSetupsLoading } from 'features/setups/selectors';
import type { IbanXsBanksRecipientsPivot } from 'features/openBanking/types';
import { useIbanXSBanksRecipientsPivotActions } from 'features/openBanking/actions';
import { withSx } from 'ui/atoms/Modal';
import ActionPanel from 'ui/molecules/ActionPanel';
import When from 'ui/support/When';
import {
    useResourceContext,
    ResourceDetail,
    type ResourceDetailProps
} from "ui/widgets/Resource";
import { IbanXSBanksRecipientsPivot } from 'ui/forms/OpenBanking';

const IbanXSBanksRecipientsPivotDetail = (props: ResourceDetailProps) => {
    const { resource, onClose } = useResourceContext<IbanXsBanksRecipientsPivot>();
    const resourceRef = useRef(resource);
    resourceRef.current = resource;

    return (
        <ResourceDetail
            {...props}
            disableCloseOnBackdropClick
            onClose={onClose}
            DialogProps={withSx({
                width: 'fit-content'
            })}
        >
            <When
                if={Boolean(resource)}
                then={useCallback(() => (
                    <IbanXSBanksRecipientsPivotForm
                        resource={resourceRef.current}
                    />
                ), [])}
            />
        </ResourceDetail>
    );
};

export default memo(IbanXSBanksRecipientsPivotDetail);

function IbanXSBanksRecipientsPivotForm({
    resource
}: {
    readonly resource: IbanXsBanksRecipientsPivot;
}) {
    const isBootstrapDataLoading = useTypedSelector(selectIsSetupsLoading(SetupIdentifier.Mors));

    const { onClose } = useResourceContext<IbanXsBanksRecipientsPivot>();
    const {
        updateIbanXSBanksRecipientsPivot
    } = useIbanXSBanksRecipientsPivotActions();

    const formProps: IbanXSBanksRecipientsPivot.IbanXSBanksRecipientsPivotForm = {
        isBootstrapDataLoading,
        enableReinitialize: false,
        bootstrapData: {},
        initialValues: IbanXSBanksRecipientsPivot.getInitialValues(resource),
        onSaveOrCreate: async ({
            iso2 = [],
            ...values
        }, formHelpers) => {
            const [request, result] = updateIbanXSBanksRecipientsPivot({
                ...values,
                iso2: iso2.at(0)
            });

            const response = (await request) as ResourceResponse<IbanXsBanksRecipientsPivot>;
            when(response.success, () => {
                onClose();
                formHelpers!.resetForm();
            });

            return result;
        }
    };

    const form = IbanXSBanksRecipientsPivot.useForm(formProps);

    return (
        <IbanXSBanksRecipientsPivot.Form
            {...form}
            {...formProps}
        >
            <ActionPanel
                isLoading={form.isSubmitting}
                onSave={async () => { }}
                onCancel={onClose}
                SaveActionProps={{
                    type: 'submit',
                    disabled: !form.isValid || !isFormChanged(form),
                    loading: form.isSubmitting
                }}
            />
        </IbanXSBanksRecipientsPivot.Form>
    );
}
